import React, { useCallback, useEffect, useMemo, useState } from 'react';
import GenericDropdown from '../GenericDropdown';
import { API_BASE_URL, getAuthHeaders, previewTransform } from '../../utils/api';

const STEP_OPS = [
  { id: 'explode', name: 'Explode array' },
  { id: 'join', name: 'Join' },
  { id: 'filter', name: 'Filter (Zagger View)' },
  { id: 'sort', name: 'Sort (Zagger View)' },
  { id: 'sql_view', name: 'SQL compute (Zagger View)' },
  { id: 'aggregate', name: 'Aggregate' },
  { id: 'calculate', name: 'Calculate' },
];

function emptyStep(op = 'explode') {
  if (op === 'explode') {
    return { op: 'explode', source: '', array_path: 'time_entries', field_map: { id: 'time_entry_id' }, keep_parent: true };
  }
  if (op === 'join') {
    return { op: 'join', left: '', right: '', how: 'left', on: [{ left: '', right: '' }], as: '' };
  }
  if (op === 'filter' || op === 'sort' || op === 'sql_view') {
    return { op, view_id: '', source: '' };
  }
  if (op === 'aggregate') {
    return {
      op: 'aggregate',
      source: '',
      group_by: [''],
      metrics: [{ fn: 'sum', field: '', as: '' }],
    };
  }
  return { op: 'calculate', source: '', fields: [{ as: '', expr: '' }] };
}

async function fetchGroups(suffix) {
  const url = suffix
    ? `${API_BASE_URL}/casc/v1/api/data-review/groups?suffix=${encodeURIComponent(suffix)}&all=true`
    : `${API_BASE_URL}/casc/v1/api/data-review/groups?all=true`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  if (!resp.ok) return [];
  const data = await resp.json();
  const list = Array.isArray(data?.groups) ? data.groups : [];
  return list.map((g) => ({
    id: g.group_slug || g.id || '',
    name: `${g.name || g.group_slug || ''} (${g.group_slug || ''})`,
  })).filter((g) => g.id);
}

async function fetchViews(suffix) {
  if (!suffix) {
    return { views: [], error: 'No horizon suffix — set an active horizon before configuring views.' };
  }
  const url = `${API_BASE_URL}/casc/v1/api/views/suffixes/${encodeURIComponent(suffix)}`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || data?.success === false) {
    return {
      views: [],
      error: data?.message || data?.error || `Failed to load views (${resp.status})`,
    };
  }
  return { views: Array.isArray(data?.data) ? data.data : [], error: null };
}

function mapViewOptions(list, type) {
  const want = String(type || '').toLowerCase();
  return (Array.isArray(list) ? list : [])
    .filter((v) => String(v?.view_type || '').toLowerCase() === want)
    .map((v) => ({
      id: String(v.id),
      name: String(v.label || v.name || v.id || '').trim() || String(v.id),
    }))
    .filter((v) => v.id);
}

const TransformConfig = ({
  value = {},
  onChange,
  suffix,
  groupSlug = null,
  excludeSlug = null,
}) => {
  const sources = useMemo(
    () => (Array.isArray(value.sources) ? value.sources : []),
    [value.sources]
  );
  const steps = useMemo(
    () => (Array.isArray(value.steps) ? value.steps : []),
    [value.steps]
  );
  const [groupOptions, setGroupOptions] = useState([]);
  const [views, setViews] = useState([]);
  const [viewsError, setViewsError] = useState('');
  const [previewRows, setPreviewRows] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let active = true;
    fetchGroups(suffix).then((list) => {
      if (!active) return;
      const filtered = excludeSlug
        ? list.filter((g) => g.id !== excludeSlug)
        : list;
      setGroupOptions(filtered);
    });
    fetchViews(suffix).then((result) => {
      if (!active) return;
      setViews(result.views || []);
      setViewsError(result.error || '');
    });
    return () => { active = false; };
  }, [suffix, excludeSlug]);

  const emit = useCallback((patch) => {
    onChange?.({
      ...value,
      type: 'transform',
      ...patch,
    });
  }, [onChange, value]);

  const aliasOptions = useMemo(
    () => sources.map((s) => ({ id: s.alias, name: s.alias })).filter((a) => a.id),
    [sources]
  );

  const filterViews = useMemo(() => mapViewOptions(views, 'filter'), [views]);
  const sortViews = useMemo(() => mapViewOptions(views, 'sort'), [views]);
  const sqlViews = useMemo(() => mapViewOptions(views, 'sql'), [views]);

  const updateSource = (idx, patch) => {
    const next = sources.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    emit({ sources: next });
  };
  const addSource = () => {
    const n = sources.length + 1;
    emit({ sources: [...sources, { alias: `src${n}`, group_slug: '' }] });
  };
  const removeSource = (idx) => {
    emit({ sources: sources.filter((_, i) => i !== idx) });
  };

  const updateStep = (idx, patch) => {
    const next = steps.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    emit({ steps: next });
  };
  const addStep = (op) => {
    emit({ steps: [...steps, emptyStep(op)] });
  };
  const removeStep = (idx) => {
    emit({ steps: steps.filter((_, i) => i !== idx) });
  };
  const moveStep = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    const tmp = next[idx];
    next[idx] = next[j];
    next[j] = tmp;
    emit({ steps: next });
  };

  const runPreview = async () => {
    if (!suffix || !groupSlug) {
      setPreviewError('Save the transformation group first, then preview.');
      return;
    }
    setPreviewing(true);
    setPreviewError('');
    try {
      const data = await previewTransform(suffix, groupSlug, {
        config: { ...value, type: 'transform', sources, steps },
        limit: 50,
      });
      setPreviewRows(data.rows || []);
      setPreviewMeta({ total: data.total, counts: data.counts, alias: data.alias });
    } catch (err) {
      setPreviewError(err.message || 'Preview failed');
      setPreviewRows(null);
    } finally {
      setPreviewing(false);
    }
  };

  const previewColumns = useMemo(() => {
    if (!previewRows || !previewRows.length) return [];
    const keys = new Set();
    previewRows.slice(0, 10).forEach((r) => {
      Object.keys(r.raw || {}).forEach((k) => keys.add(k));
    });
    return [...keys].slice(0, 12);
  }, [previewRows]);

  return (
    <div className="transform-config">
      <div className="form-row">
        <label>Source groups</label>
        <div className="hint">Each source needs an alias used by pipeline steps (e.g. toggl, projects).</div>
        {sources.map((src, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={src.alias || ''}
              onChange={(e) => updateSource(idx, { alias: e.target.value.trim() })}
              placeholder="alias"
              style={{ width: 120 }}
            />
            <div style={{ minWidth: 220, flex: 1 }}>
              <GenericDropdown
                items={groupOptions}
                value={src.group_slug || ''}
                onChange={(id) => updateSource(idx, { group_slug: id || '' })}
                placeholder="Select group"
                allowNone
                noneLabel="Select group"
              />
            </div>
            <button type="button" className="btn btn-secondary" onClick={() => removeSource(idx)}>Remove</button>
          </div>
        ))}
        <button type="button" className="btn btn-secondary" style={{ marginTop: 8 }} onClick={addSource}>
          Add source
        </button>
      </div>

      <div className="form-row">
        <label>Pipeline steps</label>
        <div className="hint">
          Explode nested arrays, join groups, apply Zagger Views (filter / sort / SQL compute), then aggregate or calculate.
        </div>
        {steps.map((step, idx) => (
          <div
            key={idx}
            style={{
              marginTop: 12,
              padding: 12,
              border: '1px solid rgba(0,0,0,0.12)',
              borderRadius: 6,
            }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>Step {idx + 1}</strong>
              <GenericDropdown
                items={STEP_OPS}
                value={step.op || 'explode'}
                onChange={(id) => updateStep(idx, emptyStep(id || 'explode'))}
                placeholder="Operation"
              />
              <button type="button" className="btn btn-secondary" onClick={() => moveStep(idx, -1)} disabled={idx === 0}>↑</button>
              <button type="button" className="btn btn-secondary" onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1}>↓</button>
              <button type="button" className="btn btn-secondary" onClick={() => removeStep(idx)}>Remove</button>
            </div>

            {step.op === 'explode' && (
              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                <GenericDropdown
                  items={aliasOptions}
                  value={step.source || ''}
                  onChange={(id) => updateStep(idx, { source: id || '' })}
                  placeholder="Source alias"
                />
                <input
                  type="text"
                  value={step.array_path || ''}
                  onChange={(e) => updateStep(idx, { array_path: e.target.value })}
                  placeholder="array_path (e.g. time_entries)"
                />
                <input
                  type="text"
                  value={Object.entries(step.field_map || {}).map(([k, v]) => `${k}:${v}`).join(', ')}
                  onChange={(e) => {
                    const map = {};
                    String(e.target.value || '').split(',').forEach((pair) => {
                      const [from, to] = pair.split(':').map((s) => s.trim());
                      if (from && to) map[from] = to;
                    });
                    updateStep(idx, { field_map: map });
                  }}
                  placeholder="field_map from:to (e.g. id:time_entry_id)"
                />
              </div>
            )}

            {step.op === 'join' && (
              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                <GenericDropdown items={aliasOptions} value={step.left || ''} onChange={(id) => updateStep(idx, { left: id || '' })} placeholder="Left alias" />
                <GenericDropdown items={aliasOptions} value={step.right || ''} onChange={(id) => updateStep(idx, { right: id || '' })} placeholder="Right alias" />
                <GenericDropdown
                  items={[{ id: 'left', name: 'Left join' }, { id: 'inner', name: 'Inner join' }]}
                  value={step.how || 'left'}
                  onChange={(id) => updateStep(idx, { how: id || 'left' })}
                  placeholder="Join type"
                />
                <input
                  type="text"
                  value={(step.on && step.on[0]) ? `${step.on[0].left || ''}=${step.on[0].right || ''}` : ''}
                  onChange={(e) => {
                    const [left, right] = String(e.target.value || '').split('=').map((s) => s.trim());
                    updateStep(idx, { on: [{ left: left || '', right: right || '' }] });
                  }}
                  placeholder="Join keys left=right (e.g. project_id=id)"
                />
                <input
                  type="text"
                  value={step.as || ''}
                  onChange={(e) => updateStep(idx, { as: e.target.value.trim() })}
                  placeholder="Output alias (default: left)"
                />
              </div>
            )}

            {(step.op === 'filter' || step.op === 'sort' || step.op === 'sql_view') && (
              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                <GenericDropdown
                  items={aliasOptions}
                  value={step.source || ''}
                  onChange={(id) => updateStep(idx, { source: id || '' })}
                  placeholder="Source alias"
                  allowNone
                  noneLabel="Primary / first"
                />
                <GenericDropdown
                  items={
                    step.op === 'filter'
                      ? filterViews
                      : step.op === 'sort'
                        ? sortViews
                        : sqlViews
                  }
                  value={step.view_id ? String(step.view_id) : ''}
                  onChange={(id) => updateStep(idx, { view_id: id || '' })}
                  placeholder={
                    step.op === 'sql_view'
                      ? 'Select SQL / compute Zagger View'
                      : `Select Zagger ${step.op} View`
                  }
                  allowNone
                  noneLabel="Select view"
                />
                {viewsError ? (
                  <div className="hint" style={{ color: 'var(--error, #c62828)' }}>
                    Could not load Views for horizon <code>{suffix || '—'}</code>: {viewsError}
                  </div>
                ) : (
                  (step.op === 'filter' ? filterViews : step.op === 'sort' ? sortViews : sqlViews).length === 0 && (
                    <div className="hint">
                      No{' '}
                      {step.op === 'sql_view' ? 'SQL compute' : step.op}{' '}
                      Views on horizon <code>{suffix || '—'}</code>. Create one under{' '}
                      <strong>Settings → Views</strong> for this same horizon, then reopen this step.
                    </div>
                  )
                )}
                <div className="hint">
                  {step.op === 'sql_view'
                    ? 'SQL Views run as SQLite SELECT against table rows (fiscal week, window sums, overtime). Create them under Settings → Views.'
                    : 'Views come from Settings → Views on this horizon.'}
                </div>
              </div>
            )}

            {step.op === 'aggregate' && (
              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                <GenericDropdown items={aliasOptions} value={step.source || ''} onChange={(id) => updateStep(idx, { source: id || '' })} placeholder="Source alias" />
                <input
                  type="text"
                  value={(step.group_by || []).join(', ')}
                  onChange={(e) => updateStep(idx, {
                    group_by: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })}
                  placeholder="group_by fields (comma-separated)"
                />
                <input
                  type="text"
                  value={(step.metrics || []).map((m) => `${m.fn}:${m.field}:${m.as || ''}`).join(' | ')}
                  onChange={(e) => {
                    const metrics = String(e.target.value || '').split('|').map((part) => {
                      const [fn, field, as] = part.split(':').map((s) => s.trim());
                      return { fn: fn || 'sum', field: field || '', as: as || '' };
                    }).filter((m) => m.field);
                    updateStep(idx, { metrics });
                  }}
                  placeholder="metrics fn:field:as (e.g. sum:seconds:hours_seconds)"
                />
              </div>
            )}

            {step.op === 'calculate' && (
              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                <GenericDropdown items={aliasOptions} value={step.source || ''} onChange={(id) => updateStep(idx, { source: id || '' })} placeholder="Source alias" />
                <input
                  type="text"
                  value={(step.fields || []).map((f) => `${f.as}=${f.expr}`).join(' | ')}
                  onChange={(e) => {
                    const fields = String(e.target.value || '').split('|').map((part) => {
                      const eq = part.indexOf('=');
                      if (eq < 0) return null;
                      return { as: part.slice(0, eq).trim(), expr: part.slice(eq + 1).trim() };
                    }).filter(Boolean);
                    updateStep(idx, { fields });
                  }}
                  placeholder="fields as=expr (e.g. labor_cost=hours_seconds / 3600 * rate)"
                />
              </div>
            )}
          </div>
        ))}
        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {STEP_OPS.map((op) => (
            <button key={op.id} type="button" className="btn btn-secondary" onClick={() => addStep(op.id)}>
              + {op.name}
            </button>
          ))}
        </div>
      </div>

      <div className="form-row">
        <button type="button" className="btn btn-secondary" onClick={runPreview} disabled={previewing || !groupSlug}>
          {previewing ? 'Previewing…' : 'Preview transform'}
        </button>
        {!groupSlug && (
          <div className="hint">Save the group first to enable preview. Horizon → Data Ingestion runs materialize.</div>
        )}
        {previewError && <div className="alert alert-error" style={{ marginTop: 8 }}>{previewError}</div>}
        {previewMeta && (
          <div className="hint" style={{ marginTop: 8 }}>
            Alias <code>{previewMeta.alias}</code> — {previewMeta.total} row(s)
            {previewMeta.counts ? ` (${Object.entries(previewMeta.counts).map(([k, v]) => `${k}:${v}`).join(', ')})` : ''}
          </div>
        )}
        {previewRows && previewRows.length > 0 && (
          <div style={{ marginTop: 8, overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', fontSize: 12 }}>
              <thead>
                <tr>
                  {previewColumns.map((c) => <th key={c}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {previewRows.slice(0, 20).map((row, i) => (
                  <tr key={i}>
                    {previewColumns.map((c) => (
                      <td key={c}>{row.raw?.[c] == null ? '' : String(row.raw[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransformConfig;
